// =============================================================================
// WIDGET GOUVERNANCE - EXIGENCES DOCUMENTAIRES
// =============================================================================
// Gestion des documents obligatoires par transition de dossier
// Configuration métier externe (collection document_requirements)
// Scope: tenant + activité
// =============================================================================

import { useState, useEffect, useCallback } from "react";
import {
  FileText,
  Plus,
  Edit2,
  Trash2,
  Save,
  X,
  ChevronDown,
  ChevronRight,
  ArrowRight,
  CheckCircle2,
  AlertCircle,
  RefreshCw,
  Search,
  Filter,
  Clock,
  User,
  Building2,
  Briefcase,
  ToggleLeft,
  ToggleRight,
  Copy,
  History
} from "lucide-react";
import { Button } from "../../../components/ui/button";
import { Badge } from "../../../components/ui/badge";
import { Input } from "../../../components/ui/input";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../../../components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../../components/ui/select";
import { toast } from "sonner";
import { enteteTenant } from "../../../utils/enteteTenant";
import { tenantResolu } from "../../../utils/tenantResolu";

const API = process.env.REACT_APP_BACKEND_URL;

// =============================================================================
// CONSTANTES
// =============================================================================

const STATUTS_DOSSIER = [
  { value: "*", label: "Tous les statuts" },
  { value: "brouillon", label: "Brouillon" },
  { value: "prospect", label: "Prospect" },
  { value: "dossier_cree", label: "Dossier créé" },
  { value: "devis_envoye", label: "Devis envoyé" },
  { value: "devis_signe", label: "Devis signé" },
  { value: "chantier_a_planifier", label: "Chantier à planifier" },
  { value: "chantier_planifie", label: "Chantier planifié" },
  { value: "chantier_en_cours", label: "Chantier en cours" },
  { value: "chantier_termine", label: "Chantier terminé" },
  { value: "conformite_en_cours", label: "Conformité en cours" },
  { value: "dossier_depose", label: "Dossier déposé" },
  { value: "aides_en_instruction", label: "Aides en instruction" },
  { value: "aides_validees", label: "Aides validées" },
  { value: "paye", label: "Payé" },
  { value: "archive", label: "Archivé" }
];

const TYPES_DOCUMENTS = [
  { code: "DEVIS_SIGNE", label: "Devis signé" },
  { code: "AVIS_IMPOSITION", label: "Avis d'imposition" },
  { code: "PHOTO_AVANT", label: "Photo avant travaux" },
  { code: "PHOTO_APRES", label: "Photo après travaux" },
  { code: "FACTURE", label: "Facture" },
  { code: "MANDAT_MPR", label: "Mandat MaPrimeRénov'" },
  { code: "MANDAT_MPR_SIGNE", label: "Mandat MPR signé" },
  { code: "CC_CEE", label: "Cadre Contribution CEE" },
  { code: "CC_CEE_SIGNE", label: "CC CEE signé" },
  { code: "ATTESTATION_HONNEUR", label: "Attestation sur l'honneur" },
  { code: "DOSSIER_COMPLET", label: "Dossier complet validé" },
  { code: "RIB", label: "RIB" },
  { code: "PIECE_IDENTITE", label: "Pièce d'identité" },
  { code: "JUSTIFICATIF_DOMICILE", label: "Justificatif de domicile" },
  { code: "AUTRE", label: "Autre document" }
];

const CONDITIONS = [
  { value: null, label: "Toujours obligatoire" },
  { value: "mpr_active", label: "Si MPR actif" },
  { value: "cee_active", label: "Si CEE actif" },
  { value: "montant_sup_10000", label: "Si montant > 10 000€" }
];

// =============================================================================
// COMPOSANT PRINCIPAL
// =============================================================================

export default function GouvDocRequirements({ tenantId }) {
  const [requirements, setRequirements] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [expandedCards, setExpandedCards] = useState({});
  
  // Dialogs
  const [editDialog, setEditDialog] = useState({ open: false, requirement: null, isNew: false });
  const [deleteDialog, setDeleteDialog] = useState({ open: false, requirement: null });
  const [docDialog, setDocDialog] = useState({ open: false, requirementCode: null, doc: null, isNew: false });
  const [historyDialog, setHistoryDialog] = useState({ open: false, requirement: null });
  const [duplicateDialog, setDuplicateDialog] = useState({ open: false, requirement: null });
  
  // Headers API
  const headers = {
    ...enteteTenant(tenantId),
    "Content-Type": "application/json"
  };

  // =============================================================================
  // CHARGEMENT DES DONNÉES
  // =============================================================================

  const loadRequirements = useCallback(async () => {
    setLoading(true);
    setError(null);
    
    try {
      const res = await fetch(`${API}/api/metier/config/document-requirements`, { headers });
      
      if (!res.ok) {
        throw new Error(`Erreur ${res.status}`);
      }
      
      const data = await res.json();
      setRequirements(data.requirements || []);
    } catch (err) {
      console.error("Erreur chargement:", err);
      setError("Impossible de charger les configurations");
      toast.error("Erreur de chargement");
    } finally {
      setLoading(false);
    }
  }, [tenantId]);

  useEffect(() => {
    loadRequirements();
  }, [loadRequirements]);

  // =============================================================================
  // ACTIONS CRUD
  // =============================================================================

  const handleSaveRequirement = async (requirement) => {
    try {
      const isNew = !requirements.find(r => r.code === requirement.code);
      const method = isNew ? "POST" : "PATCH";
      const url = isNew 
        ? `${API}/api/metier/config/document-requirements`
        : `${API}/api/metier/config/document-requirements/${requirement.code}`;
      
      const res = await fetch(url, {
        method,
        headers,
        body: JSON.stringify(requirement)
      });
      
      if (!res.ok) {
        const err = await res.json();
        throw new Error(err.detail || "Erreur lors de l'enregistrement");
      }
      
      toast.success(isNew ? "Configuration créée" : "Configuration mise à jour");
      setEditDialog({ open: false, requirement: null, isNew: false });
      loadRequirements();
    } catch (err) {
      toast.error(err.message);
    }
  };

  const handleDeleteRequirement = async (code) => {
    try {
      const res = await fetch(`${API}/api/metier/config/document-requirements/${code}`, {
        method: "DELETE",
        headers
      });
      
      if (!res.ok) {
        throw new Error("Erreur lors de la suppression");
      }
      
      toast.success("Configuration supprimée");
      setDeleteDialog({ open: false, requirement: null });
      loadRequirements();
    } catch (err) {
      toast.error(err.message);
    }
  };

  const handleToggleRequirement = async (code, currentEnabled) => {
    try {
      const res = await fetch(`${API}/api/metier/config/document-requirements/${code}/toggle`, {
        method: "POST",
        headers
      });
      
      if (!res.ok) {
        throw new Error("Erreur lors du basculement");
      }
      
      toast.success(currentEnabled ? "Configuration désactivée" : "Configuration activée");
      loadRequirements();
    } catch (err) {
      toast.error(err.message);
    }
  };

  const handleDuplicateRequirement = async (sourceCode, duplicateData) => {
    try {
      const res = await fetch(`${API}/api/metier/config/document-requirements/${sourceCode}/duplicate`, {
        method: "POST",
        headers,
        body: JSON.stringify(duplicateData)
      });
      
      if (!res.ok) {
        const err = await res.json();
        throw new Error(err.detail || "Erreur lors de la duplication");
      }
      
      const data = await res.json();
      toast.success(`Configuration dupliquée: ${data.requirement.code}`);
      setDuplicateDialog({ open: false, requirement: null });
      loadRequirements();
    } catch (err) {
      toast.error(err.message);
    }
  };

  const handleAddDocument = async (reqCode, doc) => {
    try {
      const res = await fetch(`${API}/api/metier/config/document-requirements/${reqCode}/documents`, {
        method: "POST",
        headers,
        body: JSON.stringify(doc)
      });
      
      if (!res.ok) {
        const err = await res.json();
        throw new Error(err.detail || "Erreur");
      }
      
      toast.success("Document ajouté");
      setDocDialog({ open: false, requirementCode: null, doc: null, isNew: false });
      loadRequirements();
    } catch (err) {
      toast.error(err.message);
    }
  };

  const handleRemoveDocument = async (reqCode, docCode) => {
    try {
      const res = await fetch(
        `${API}/api/metier/config/document-requirements/${reqCode}/documents/${docCode}`,
        { method: "DELETE", headers }
      );
      
      if (!res.ok) {
        throw new Error("Erreur lors de la suppression");
      }
      
      toast.success("Document retiré");
      loadRequirements();
    } catch (err) {
      toast.error(err.message);
    }
  };

  // =============================================================================
  // FILTRAGE
  // =============================================================================

  const filteredRequirements = requirements.filter(req => {
    if (!searchTerm) return true;
    const search = searchTerm.toLowerCase();
    return (
      req.code?.toLowerCase().includes(search) ||
      req.name?.toLowerCase().includes(search) ||
      req.description?.toLowerCase().includes(search)
    );
  });

  // =============================================================================
  // HELPERS
  // =============================================================================

  const getStatutLabel = (value) => {
    if (value === "*") return "Tous";
    return STATUTS_DOSSIER.find(s => s.value === value)?.label || value;
  };

  const toggleCard = (code) => {
    setExpandedCards(prev => ({ ...prev, [code]: !prev[code] }));
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return "N/A";
    return new Date(dateStr).toLocaleDateString("fr-FR", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit"
    });
  };

  // =============================================================================
  // RENDU
  // =============================================================================

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <RefreshCw className="w-8 h-8 text-blue-400 animate-spin" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex flex-col items-center justify-center h-64 gap-4">
        <AlertCircle className="w-12 h-12 text-red-400" />
        <p className="text-red-400">{error}</p>
        <Button onClick={loadRequirements} variant="outline">
          <RefreshCw className="w-4 h-4 mr-2" />
          Réessayer
        </Button>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="gouv-doc-requirements">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-lg font-semibold text-white flex items-center gap-2">
            <FileText className="w-5 h-5 text-indigo-400" />
            Exigences Documentaires
          </h3>
          <p className="text-sm text-slate-400 mt-1">
            Configuration des documents obligatoires par transition de dossier
          </p>
        </div>
        
        <div className="flex items-center gap-2">
          <Button 
            onClick={loadRequirements}
            variant="ghost"
            size="sm"
          >
            <RefreshCw className="w-4 h-4" />
          </Button>
          <Button
            onClick={() => setEditDialog({ 
              open: true, 
              requirement: {
                code: "",
                name: "",
                description: "",
                from_states: ["*"],
                to_states: [],
                documents: [],
                enabled: true,
                tenant_scope: ["*"],
                activite_scope: ["*"]
              },
              isNew: true 
            })}
            className="bg-indigo-600 hover:bg-indigo-700"
          >
            <Plus className="w-4 h-4 mr-2" />
            Nouvelle Configuration
          </Button>
        </div>
      </div>

      {/* Barre de recherche */}
      <div className="relative">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
        <Input
          placeholder="Rechercher une configuration..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className="pl-10 bg-slate-800 border-slate-700"
        />
      </div>

      {/* Info box */}
      <div className="p-3 bg-blue-500/10 border border-blue-500/20 rounded-lg">
        <p className="text-blue-300 text-sm">
          <AlertCircle className="w-4 h-4 inline mr-2" />
          Ces configurations définissent les documents requis pour chaque transition de statut.
          Si aucune configuration n'existe pour une transition, le comportement par défaut est <strong>PASS</strong>.
        </p>
      </div>

      {/* Liste des configurations */}
      <div className="space-y-4">
        {filteredRequirements.length === 0 ? (
          <div className="text-center py-12 text-slate-400">
            <FileText className="w-12 h-12 mx-auto mb-4 opacity-50" />
            <p>Aucune configuration trouvée</p>
          </div>
        ) : (
          filteredRequirements.map((req) => (
            <RequirementCard
              key={req.code}
              requirement={req}
              expanded={expandedCards[req.code]}
              onToggleExpand={() => toggleCard(req.code)}
              onEdit={() => setEditDialog({ open: true, requirement: req, isNew: false })}
              onDelete={() => setDeleteDialog({ open: true, requirement: req })}
              onDuplicate={() => setDuplicateDialog({ open: true, requirement: req })}
              onToggleEnabled={() => handleToggleRequirement(req.code, req.enabled)}
              onAddDoc={() => setDocDialog({ open: true, requirementCode: req.code, doc: null, isNew: true })}
              onRemoveDoc={(docCode) => handleRemoveDocument(req.code, docCode)}
              onShowHistory={() => setHistoryDialog({ open: true, requirement: req })}
              getStatutLabel={getStatutLabel}
              formatDate={formatDate}
            />
          ))
        )}
      </div>

      {/* Dialog édition configuration */}
      <RequirementEditDialog
        open={editDialog.open}
        requirement={editDialog.requirement}
        isNew={editDialog.isNew}
        onClose={() => setEditDialog({ open: false, requirement: null, isNew: false })}
        onSave={handleSaveRequirement}
      />

      {/* Dialog duplication */}
      <DuplicateDialog
        open={duplicateDialog.open}
        requirement={duplicateDialog.requirement}
        onClose={() => setDuplicateDialog({ open: false, requirement: null })}
        onDuplicate={(data) => handleDuplicateRequirement(duplicateDialog.requirement?.code, data)}
      />

      {/* Dialog suppression */}
      <Dialog open={deleteDialog.open} onOpenChange={(open) => !open && setDeleteDialog({ open: false, requirement: null })}>
        <DialogContent className="bg-slate-900 border-slate-700">
          <DialogHeader>
            <DialogTitle className="text-red-400">Supprimer la configuration</DialogTitle>
            <DialogDescription>
              Êtes-vous sûr de vouloir supprimer la configuration <strong>{deleteDialog.requirement?.code}</strong> ?
              Cette action est irréversible.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setDeleteDialog({ open: false, requirement: null })}>
              Annuler
            </Button>
            <Button 
              className="bg-red-600 hover:bg-red-700"
              onClick={() => handleDeleteRequirement(deleteDialog.requirement?.code)}
            >
              Supprimer
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Dialog ajout document */}
      <DocumentEditDialog
        open={docDialog.open}
        doc={docDialog.doc}
        isNew={docDialog.isNew}
        onClose={() => setDocDialog({ open: false, requirementCode: null, doc: null, isNew: false })}
        onSave={(doc) => handleAddDocument(docDialog.requirementCode, doc)}
      />

      {/* Dialog historique */}
      <Dialog open={historyDialog.open} onOpenChange={(open) => !open && setHistoryDialog({ open: false, requirement: null })}>
        <DialogContent className="bg-slate-900 border-slate-700 max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <History className="w-5 h-5 text-slate-400" />
              Historique des modifications
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-3 py-4">
            {historyDialog.requirement?.created_at && (
              <div className="flex items-start gap-3 p-3 bg-slate-800 rounded-lg">
                <CheckCircle2 className="w-5 h-5 text-green-400 mt-0.5" />
                <div>
                  <p className="text-white text-sm font-medium">Création</p>
                  <p className="text-slate-400 text-xs">
                    {formatDate(historyDialog.requirement.created_at)} par {historyDialog.requirement.created_by || "system"}
                  </p>
                </div>
              </div>
            )}
            {historyDialog.requirement?.modified_at && (
              <div className="flex items-start gap-3 p-3 bg-slate-800 rounded-lg">
                <Edit2 className="w-5 h-5 text-amber-400 mt-0.5" />
                <div>
                  <p className="text-white text-sm font-medium">Dernière modification</p>
                  <p className="text-slate-400 text-xs">
                    {formatDate(historyDialog.requirement.modified_at)} par {historyDialog.requirement.modified_by || "system"}
                  </p>
                </div>
              </div>
            )}
            {!historyDialog.requirement?.modified_at && (
              <p className="text-slate-500 text-sm text-center py-4">
                Aucune modification depuis la création
              </p>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}


// =============================================================================
// COMPOSANT CARTE CONFIGURATION
// =============================================================================

function RequirementCard({
  requirement,
  expanded,
  onToggleExpand,
  onEdit,
  onDelete,
  onDuplicate,
  onToggleEnabled,
  onAddDoc,
  onRemoveDoc,
  onShowHistory,
  getStatutLabel,
  formatDate
}) {
  const { code, name, description, from_states, to_states, documents, enabled, tenant_scope, activite_scope } = requirement;
  
  return (
    <div 
      className={`border rounded-lg overflow-hidden transition-all ${
        enabled 
          ? "bg-slate-800/50 border-slate-700" 
          : "bg-slate-900/50 border-slate-800 opacity-60"
      }`}
      data-testid={`requirement-card-${code}`}
    >
      {/* Header */}
      <div 
        className="flex items-center justify-between p-4 cursor-pointer hover:bg-slate-700/30"
        onClick={onToggleExpand}
      >
        <div className="flex items-center gap-3">
          {expanded ? (
            <ChevronDown className="w-5 h-5 text-slate-400" />
          ) : (
            <ChevronRight className="w-5 h-5 text-slate-400" />
          )}
          
          <div>
            <div className="flex items-center gap-2">
              <h4 className="text-white font-medium">{name}</h4>
              <Badge className="bg-slate-700 text-slate-300 text-xs">{code}</Badge>
              {!enabled && (
                <Badge className="bg-red-500/20 text-red-400 text-xs">Désactivé</Badge>
              )}
            </div>
            {description && (
              <p className="text-slate-400 text-sm mt-1">{description}</p>
            )}
          </div>
        </div>
        
        <div className="flex items-center gap-4" onClick={(e) => e.stopPropagation()}>
          {/* Transition */}
          <div className="flex items-center gap-2 text-sm">
            <Badge className="bg-blue-500/20 text-blue-300">
              {from_states?.includes("*") ? "Tous" : from_states?.map(s => getStatutLabel(s)).join(", ")}
            </Badge>
            <ArrowRight className="w-4 h-4 text-slate-500" />
            <Badge className="bg-green-500/20 text-green-300">
              {to_states?.includes("*") ? "Tous" : to_states?.map(s => getStatutLabel(s)).join(", ")}
            </Badge>
          </div>
          
          {/* Documents count */}
          <Badge className="bg-indigo-500/20 text-indigo-300">
            {documents?.length || 0} doc{documents?.length > 1 ? "s" : ""}
          </Badge>
          
          {/* Actions */}
          <div className="flex items-center gap-1">
            <Button
              variant="ghost"
              size="sm"
              onClick={onToggleEnabled}
              title={enabled ? "Désactiver" : "Activer"}
            >
              {enabled ? (
                <ToggleRight className="w-4 h-4 text-green-400" />
              ) : (
                <ToggleLeft className="w-4 h-4 text-slate-400" />
              )}
            </Button>
            <Button variant="ghost" size="sm" onClick={onDuplicate} title="Dupliquer">
              <Copy className="w-4 h-4 text-cyan-400" />
            </Button>
            <Button variant="ghost" size="sm" onClick={onShowHistory} title="Historique">
              <History className="w-4 h-4 text-slate-400" />
            </Button>
            <Button variant="ghost" size="sm" onClick={onEdit} title="Modifier">
              <Edit2 className="w-4 h-4 text-slate-400" />
            </Button>
            <Button variant="ghost" size="sm" onClick={onDelete} title="Supprimer">
              <Trash2 className="w-4 h-4 text-red-400" />
            </Button>
          </div>
        </div>
      </div>
      
      {/* Contenu déplié */}
      {expanded && (
        <div className="px-4 pb-4 border-t border-slate-700/50">
          {/* Info traçabilité duplication */}
          {requirement.duplicated_from && (
            <div className="flex items-center gap-2 py-2 px-3 mb-3 bg-cyan-500/10 border border-cyan-500/20 rounded-lg text-sm">
              <Copy className="w-4 h-4 text-cyan-400" />
              <span className="text-cyan-300">
                Dupliqué de <strong>{requirement.duplicated_from.code}</strong>
              </span>
              <span className="text-cyan-400/60 text-xs">
                ({requirement.duplicated_from.duplicated_at ? new Date(requirement.duplicated_from.duplicated_at).toLocaleDateString("fr-FR") : ""})
              </span>
            </div>
          )}
          
          {/* Scope */}
          <div className="flex items-center gap-6 py-3 text-sm">
            <div className="flex items-center gap-2">
              <Building2 className="w-4 h-4 text-slate-500" />
              <span className="text-slate-400">Tenants:</span>
              <span className="text-slate-300">
                {tenant_scope?.includes("*") ? "Tous" : tenant_scope?.join(", ")}
              </span>
            </div>
            <div className="flex items-center gap-2">
              <Briefcase className="w-4 h-4 text-slate-500" />
              <span className="text-slate-400">Activités:</span>
              <span className="text-slate-300">
                {activite_scope?.includes("*") ? "Toutes" : activite_scope?.join(", ")}
              </span>
            </div>
          </div>
          
          {/* Documents */}
          <div className="mt-2">
            <div className="flex items-center justify-between mb-2">
              <h5 className="text-sm font-medium text-slate-300">Documents requis</h5>
              <Button variant="ghost" size="sm" onClick={onAddDoc}>
                <Plus className="w-4 h-4 mr-1" />
                Ajouter
              </Button>
            </div>
            
            {documents?.length === 0 ? (
              <p className="text-slate-500 text-sm py-4 text-center">
                Aucun document requis (PASS par défaut)
              </p>
            ) : (
              <div className="space-y-2">
                {documents?.map((doc, idx) => (
                  <div 
                    key={idx}
                    className="flex items-center justify-between p-2 bg-slate-700/30 rounded-lg"
                  >
                    <div className="flex items-center gap-3">
                      <FileText className="w-4 h-4 text-indigo-400" />
                      <div>
                        <span className="text-white text-sm">{doc.label || doc.code}</span>
                        <span className="text-slate-500 text-xs ml-2">({doc.code})</span>
                      </div>
                      {doc.required ? (
                        <Badge className="bg-green-500/20 text-green-400 text-xs">Obligatoire</Badge>
                      ) : doc.required_if ? (
                        <Badge className="bg-amber-500/20 text-amber-400 text-xs">
                          Si {doc.required_if}
                        </Badge>
                      ) : (
                        <Badge className="bg-slate-500/20 text-slate-400 text-xs">Optionnel</Badge>
                      )}
                    </div>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => onRemoveDoc(doc.code)}
                      className="text-red-400 hover:text-red-300"
                    >
                      <X className="w-4 h-4" />
                    </Button>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}


// =============================================================================
// DIALOG ÉDITION CONFIGURATION
// =============================================================================

function RequirementEditDialog({ open, requirement, isNew, onClose, onSave }) {
  const [form, setForm] = useState(requirement || {});
  
  useEffect(() => {
    if (requirement) {
      setForm(requirement);
    }
  }, [requirement]);
  
  const handleSubmit = () => {
    if (!form.code || !form.name) {
      toast.error("Code et nom sont obligatoires");
      return;
    }
    onSave(form);
  };
  
  const updateFromStates = (value) => {
    if (value === "*") {
      setForm(prev => ({ ...prev, from_states: ["*"] }));
    } else {
      const current = form.from_states?.filter(s => s !== "*") || [];
      if (current.includes(value)) {
        setForm(prev => ({ ...prev, from_states: current.filter(s => s !== value) }));
      } else {
        setForm(prev => ({ ...prev, from_states: [...current, value] }));
      }
    }
  };
  
  const updateToStates = (value) => {
    if (value === "*") {
      setForm(prev => ({ ...prev, to_states: ["*"] }));
    } else {
      const current = form.to_states?.filter(s => s !== "*") || [];
      if (current.includes(value)) {
        setForm(prev => ({ ...prev, to_states: current.filter(s => s !== value) }));
      } else {
        setForm(prev => ({ ...prev, to_states: [...current, value] }));
      }
    }
  };
  
  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="bg-slate-900 border-slate-700 max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>
            {isNew ? "Nouvelle configuration" : `Modifier ${requirement?.code}`}
          </DialogTitle>
          <DialogDescription>
            Définissez les transitions et les documents requis
          </DialogDescription>
        </DialogHeader>
        
        <div className="space-y-4 py-4">
          {/* Code et Nom */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className="text-sm text-slate-400 mb-1 block">Code *</label>
              <Input
                value={form.code || ""}
                onChange={(e) => setForm(prev => ({ ...prev, code: e.target.value.toUpperCase().replace(/\s/g, "-") }))}
                placeholder="DOC-REQ-XXX"
                disabled={!isNew}
                className="bg-slate-800 border-slate-700"
              />
            </div>
            <div>
              <label className="text-sm text-slate-400 mb-1 block">Nom *</label>
              <Input
                value={form.name || ""}
                onChange={(e) => setForm(prev => ({ ...prev, name: e.target.value }))}
                placeholder="Documents pour..."
                className="bg-slate-800 border-slate-700"
              />
            </div>
          </div>
          
          {/* Description */}
          <div>
            <label className="text-sm text-slate-400 mb-1 block">Description</label>
            <Input
              value={form.description || ""}
              onChange={(e) => setForm(prev => ({ ...prev, description: e.target.value }))}
              placeholder="Description de cette configuration..."
              className="bg-slate-800 border-slate-700"
            />
          </div>
          
          {/* Transitions */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className="text-sm text-slate-400 mb-1 block">Statuts de départ</label>
              <div className="space-y-1 max-h-40 overflow-y-auto p-2 bg-slate-800 rounded border border-slate-700">
                {STATUTS_DOSSIER.map(s => (
                  <label key={s.value} className="flex items-center gap-2 cursor-pointer hover:bg-slate-700 p-1 rounded">
                    <input
                      type="checkbox"
                      checked={form.from_states?.includes(s.value) || (s.value === "*" && form.from_states?.includes("*"))}
                      onChange={() => updateFromStates(s.value)}
                      className="rounded"
                    />
                    <span className="text-sm text-slate-300">{s.label}</span>
                  </label>
                ))}
              </div>
            </div>
            <div>
              <label className="text-sm text-slate-400 mb-1 block">Statuts d'arrivée</label>
              <div className="space-y-1 max-h-40 overflow-y-auto p-2 bg-slate-800 rounded border border-slate-700">
                {STATUTS_DOSSIER.filter(s => s.value !== "*").map(s => (
                  <label key={s.value} className="flex items-center gap-2 cursor-pointer hover:bg-slate-700 p-1 rounded">
                    <input
                      type="checkbox"
                      checked={form.to_states?.includes(s.value)}
                      onChange={() => updateToStates(s.value)}
                      className="rounded"
                    />
                    <span className="text-sm text-slate-300">{s.label}</span>
                  </label>
                ))}
              </div>
            </div>
          </div>
          
          {/* Scope */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className="text-sm text-slate-400 mb-1 block">Scope Tenants</label>
              <Input
                value={form.tenant_scope?.join(", ") || "*"}
                onChange={(e) => setForm(prev => ({ 
                  ...prev, 
                  tenant_scope: e.target.value.split(",").map(s => s.trim()).filter(Boolean) 
                }))}
                placeholder="* pour tous"
                className="bg-slate-800 border-slate-700"
              />
              <p className="text-xs text-slate-500 mt-1">Séparez par des virgules, * = tous</p>
            </div>
            <div>
              <label className="text-sm text-slate-400 mb-1 block">Scope Activités</label>
              <Input
                value={form.activite_scope?.join(", ") || "*"}
                onChange={(e) => setForm(prev => ({ 
                  ...prev, 
                  activite_scope: e.target.value.split(",").map(s => s.trim()).filter(Boolean) 
                }))}
                placeholder="* pour toutes"
                className="bg-slate-800 border-slate-700"
              />
              <p className="text-xs text-slate-500 mt-1">Séparez par des virgules, * = toutes</p>
            </div>
          </div>
        </div>
        
        <DialogFooter>
          <Button variant="ghost" onClick={onClose}>Annuler</Button>
          <Button onClick={handleSubmit} className="bg-indigo-600 hover:bg-indigo-700">
            <Save className="w-4 h-4 mr-2" />
            {isNew ? "Créer" : "Enregistrer"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}


// =============================================================================
// DIALOG AJOUT DOCUMENT
// =============================================================================

function DocumentEditDialog({ open, doc, isNew, onClose, onSave }) {
  const [form, setForm] = useState(doc || { code: "", label: "", required: true, required_if: null });
  
  useEffect(() => {
    if (doc) {
      setForm(doc);
    } else {
      setForm({ code: "", label: "", required: true, required_if: null });
    }
  }, [doc, open]);
  
  const handleSubmit = () => {
    if (!form.code) {
      toast.error("Le code du document est obligatoire");
      return;
    }
    onSave(form);
  };
  
  const handleSelectType = (code) => {
    const type = TYPES_DOCUMENTS.find(t => t.code === code);
    if (type) {
      setForm(prev => ({ ...prev, code: type.code, label: type.label }));
    }
  };
  
  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="bg-slate-900 border-slate-700">
        <DialogHeader>
          <DialogTitle>Ajouter un document requis</DialogTitle>
        </DialogHeader>
        
        <div className="space-y-4 py-4">
          {/* Sélection type prédéfini */}
          <div>
            <label className="text-sm text-slate-400 mb-1 block">Type de document</label>
            <Select value={form.code} onValueChange={handleSelectType}>
              <SelectTrigger className="bg-slate-800 border-slate-700">
                <SelectValue placeholder="Sélectionner un type..." />
              </SelectTrigger>
              <SelectContent className="bg-slate-800 border-slate-700">
                {TYPES_DOCUMENTS.map(type => (
                  <SelectItem key={type.code} value={type.code}>
                    {type.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          
          {/* Code personnalisé */}
          <div>
            <label className="text-sm text-slate-400 mb-1 block">Code *</label>
            <Input
              value={form.code || ""}
              onChange={(e) => setForm(prev => ({ ...prev, code: e.target.value.toUpperCase() }))}
              placeholder="DEVIS_SIGNE"
              className="bg-slate-800 border-slate-700"
            />
          </div>
          
          {/* Label */}
          <div>
            <label className="text-sm text-slate-400 mb-1 block">Libellé</label>
            <Input
              value={form.label || ""}
              onChange={(e) => setForm(prev => ({ ...prev, label: e.target.value }))}
              placeholder="Devis signé par le client"
              className="bg-slate-800 border-slate-700"
            />
          </div>
          
          {/* Condition */}
          <div>
            <label className="text-sm text-slate-400 mb-1 block">Condition</label>
            <Select 
              value={form.required_if || (form.required ? "required" : "optional")} 
              onValueChange={(v) => {
                if (v === "required") {
                  setForm(prev => ({ ...prev, required: true, required_if: null }));
                } else if (v === "optional") {
                  setForm(prev => ({ ...prev, required: false, required_if: null }));
                } else {
                  setForm(prev => ({ ...prev, required: false, required_if: v }));
                }
              }}
            >
              <SelectTrigger className="bg-slate-800 border-slate-700">
                <SelectValue />
              </SelectTrigger>
              <SelectContent className="bg-slate-800 border-slate-700">
                <SelectItem value="required">Toujours obligatoire</SelectItem>
                <SelectItem value="optional">Optionnel</SelectItem>
                <SelectItem value="mpr_active">Obligatoire si MPR actif</SelectItem>
                <SelectItem value="cee_active">Obligatoire si CEE actif</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>
        
        <DialogFooter>
          <Button variant="ghost" onClick={onClose}>Annuler</Button>
          <Button onClick={handleSubmit} className="bg-indigo-600 hover:bg-indigo-700">
            <Plus className="w-4 h-4 mr-2" />
            Ajouter
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}


// =============================================================================
// DIALOG DUPLICATION
// =============================================================================

function DuplicateDialog({ open, requirement, onClose, onDuplicate }) {
  const [form, setForm] = useState({
    new_code: "",
    new_name: "",
    tenant_scope: ["*"],
    activite_scope: ["*"]
  });
  const [checking, setChecking] = useState(false);
  const [conflict, setConflict] = useState(null);

  useEffect(() => {
    if (requirement && open) {
      // Suggérer un nouveau code
      setForm({
        new_code: `${requirement.code}-COPY`,
        new_name: `${requirement.name} (copie)`,
        tenant_scope: requirement.tenant_scope || ["*"],
        activite_scope: requirement.activite_scope || ["*"]
      });
      setConflict(null);
    }
  }, [requirement, open]);

  const checkConflict = async () => {
    if (!requirement) return;
    
    setChecking(true);
    try {
      const tenantParam = form.tenant_scope.join(",");
      const activiteParam = form.activite_scope.join(",");
      
      const res = await fetch(
        `${API}/api/metier/config/document-requirements/${requirement.code}/can-duplicate?new_tenant_scope=${tenantParam}&new_activite_scope=${activiteParam}`,
        { headers: { ...enteteTenant(tenantResolu()) } }
      );
      
      if (res.ok) {
        const data = await res.json();
        if (!data.can_duplicate) {
          setConflict(data.conflict_with);
        } else {
          setConflict(null);
        }
      }
    } catch (err) {
      console.error("Erreur vérification conflit:", err);
    } finally {
      setChecking(false);
    }
  };

  // Vérifier les conflits quand le scope change
  useEffect(() => {
    if (open && requirement) {
      const timer = setTimeout(checkConflict, 500);
      return () => clearTimeout(timer);
    }
  }, [form.tenant_scope, form.activite_scope, open]);

  const handleSubmit = () => {
    // Validation
    if (!form.new_code || form.new_code === requirement?.code) {
      toast.error("Le nouveau code est obligatoire et doit être différent");
      return;
    }
    if (!form.new_name || form.new_name === requirement?.name) {
      toast.error("Le nouveau nom est obligatoire et doit être différent");
      return;
    }
    if (conflict) {
      toast.error("Résolvez le conflit avant de dupliquer");
      return;
    }
    
    onDuplicate(form);
  };

  if (!requirement) return null;

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="bg-slate-900 border-slate-700 max-w-lg">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Copy className="w-5 h-5 text-cyan-400" />
            Dupliquer la configuration
          </DialogTitle>
          <DialogDescription>
            Créer une variante de <strong>{requirement.code}</strong> pour un tenant ou une activité spécifique
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-4">
          {/* Source info */}
          <div className="p-3 bg-slate-800 rounded-lg">
            <p className="text-xs text-slate-400 uppercase tracking-wide mb-2">Configuration source</p>
            <div className="flex items-center gap-2">
              <Badge className="bg-slate-700">{requirement.code}</Badge>
              <span className="text-white">{requirement.name}</span>
            </div>
            <p className="text-slate-400 text-sm mt-1">
              {requirement.documents?.length || 0} documents • 
              {requirement.from_states?.join(", ")} → {requirement.to_states?.join(", ")}
            </p>
          </div>

          {/* Nouveau code */}
          <div>
            <label className="text-sm text-slate-400 mb-1 block">Nouveau code *</label>
            <Input
              value={form.new_code}
              onChange={(e) => setForm(prev => ({ ...prev, new_code: e.target.value.toUpperCase().replace(/\s/g, "-") }))}
              placeholder="DOC-REQ-XXX-TENANT"
              className="bg-slate-800 border-slate-700"
            />
            <p className="text-xs text-slate-500 mt-1">Doit être différent du code source</p>
          </div>

          {/* Nouveau nom */}
          <div>
            <label className="text-sm text-slate-400 mb-1 block">Nouveau nom *</label>
            <Input
              value={form.new_name}
              onChange={(e) => setForm(prev => ({ ...prev, new_name: e.target.value }))}
              placeholder="Documents pour... (variante)"
              className="bg-slate-800 border-slate-700"
            />
            <p className="text-xs text-slate-500 mt-1">Doit être différent du nom source</p>
          </div>

          {/* Scope tenant */}
          <div>
            <label className="text-sm text-slate-400 mb-1 block">Scope Tenants</label>
            <Input
              value={form.tenant_scope.join(", ")}
              onChange={(e) => setForm(prev => ({ 
                ...prev, 
                tenant_scope: e.target.value.split(",").map(s => s.trim()).filter(Boolean) || ["*"]
              }))}
              placeholder="tenant-a, tenant-b"
              className="bg-slate-800 border-slate-700"
            />
            <p className="text-xs text-slate-500 mt-1">* = tous les tenants</p>
          </div>

          {/* Scope activité */}
          <div>
            <label className="text-sm text-slate-400 mb-1 block">Scope Activités</label>
            <Input
              value={form.activite_scope.join(", ")}
              onChange={(e) => setForm(prev => ({ 
                ...prev, 
                activite_scope: e.target.value.split(",").map(s => s.trim()).filter(Boolean) || ["*"]
              }))}
              placeholder="climatisation, chauffage"
              className="bg-slate-800 border-slate-700"
            />
            <p className="text-xs text-slate-500 mt-1">* = toutes les activités</p>
          </div>

          {/* Alerte conflit */}
          {checking && (
            <div className="flex items-center gap-2 text-slate-400 text-sm">
              <RefreshCw className="w-4 h-4 animate-spin" />
              Vérification des conflits...
            </div>
          )}
          
          {conflict && (
            <div className="p-3 bg-red-500/10 border border-red-500/30 rounded-lg">
              <div className="flex items-start gap-2">
                <AlertCircle className="w-5 h-5 text-red-400 mt-0.5" />
                <div>
                  <p className="text-red-300 font-medium">Conflit détecté</p>
                  <p className="text-red-200 text-sm mt-1">
                    La configuration <strong>{conflict.code}</strong> couvre déjà les mêmes 
                    transitions pour ce scope tenant/activité.
                  </p>
                  <p className="text-red-300 text-xs mt-2">
                    Modifiez le scope pour éviter le conflit.
                  </p>
                </div>
              </div>
            </div>
          )}

          {/* Info traçabilité */}
          <div className="p-3 bg-cyan-500/10 border border-cyan-500/20 rounded-lg">
            <p className="text-cyan-300 text-sm">
              <Copy className="w-4 h-4 inline mr-2" />
              La nouvelle configuration gardera une trace de la source (champ <code>duplicated_from</code>)
            </p>
          </div>
        </div>

        <DialogFooter>
          <Button variant="ghost" onClick={onClose}>Annuler</Button>
          <Button 
            onClick={handleSubmit} 
            className="bg-cyan-600 hover:bg-cyan-700"
            disabled={checking || !!conflict}
          >
            <Copy className="w-4 h-4 mr-2" />
            Dupliquer
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
